import React, { useState, useEffect, useRef, SyntheticEvent } from 'react';

import { IPageFields } from '../../../../types/contentful';

import { usePageDataContext } from '../../../context/PageData';
import ConsentService from '../../../services/consent';
import { trackWithFingerprint } from '../../../utils/trackWithFingerprint';
import { trackRumEvent } from '../../../utils/datadogRum';
import { getAnonymousId } from '../../../utils/getAnonymousId';
import errors from '../../../utils/errors';
import { phoneNumberRegex } from '../../../utils/regex';
import { getRedirectByEnv, appendParams } from '../../../utils/buildWebRegUrl';
import analytics from '../../../utils/analytics';

import CtaWidgetMap from '../CtaWidgetMap';

import {
  SharedProps,
  InputProps,
  DEFAULT_LABEL_V1,
  DEFAULT_PLACEHOLDER_V1,
  ctaWidgetEnum,
  PartialCtaProps,
  RegistrationEntrypoint
} from '../shared';

import { Wrapper, Container, FlexForm, Error } from '../styles';

interface WebRegWidgetProps extends SharedProps, RegistrationEntrypoint {
  widgetProps: PartialCtaProps;
  widgetType?: ctaWidgetEnum;
  module?: string;
  height?: string | number;
  width?: string | number;
  bypassPhoneNumberEntry?: boolean;
}

export function WebRegWidget({
  id,
  className,
  module,
  widgetProps,
  forwardedCtaParams,
  widgetType = ctaWidgetEnum.GET_STARTED,
  labelText = DEFAULT_LABEL_V1,
  placeholderText = DEFAULT_PLACEHOLDER_V1,
  errorPosition = 'bottom',
  showErrorBanner = true,
  sendConsentEvent = false,
  height = '100%',
  width = 'fit-content',
  bypassPhoneNumberEntry = false
}: WebRegWidgetProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef<any>(null);

  const { page } = usePageDataContext();
  const { webRegUrl, stagingWebRegUrl, previewWebRegUrl, forwardedUrlParams } =
    (page as IPageFields) ?? {};

  useEffect(() => {
    const setFromEvent = () => setErrorMessage('');
    window.addEventListener('mousedown', setFromEvent);

    return () => {
      window.removeEventListener('mousedown', setFromEvent);
    };
  }, []);

  const onSuccess = (phone: string) => {
    const anonymousId = getAnonymousId();

    trackRumEvent('user_to_registration', {
      id: anonymousId,
      anonymousId,
      phoneNumber: phone
    });

    const baseUrl = getRedirectByEnv({
      prod: webRegUrl as string,
      stage: stagingWebRegUrl as string,
      preview: previewWebRegUrl as string,
      development: previewWebRegUrl as string
    }) as string;

    const registration = appendParams({
      baseUrl,
      phoneNumber: phone,
      forwardedUrlParams: forwardedUrlParams as string[],
      bypassPhoneNumberEntry,
      forwardedCtaParams
    });

    window.location.assign(registration);
  };

  const onError = (err: string) => {
    setErrorMessage(err);
  };

  const fireAnalytics = async () => {
    if (bypassPhoneNumberEntry) {
      await trackWithFingerprint('CTA Clicked', {
        page_url: window.location.href,
        module
      });
    } else {
      await trackWithFingerprint('Phone Number Entered', {
        page_url: window.location.href
      });
    }

    if (sendConsentEvent) {
      const userId = analytics.anonymousId || false;

      await ConsentService.collectConsent({
        userId,
        type: 'affiliate_SEC_disclaimer',
        pageName: 'Affiliate Page'
      });
    }
  };

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    const unformattedPhoneNumber = phoneNumber.replace(/\D/g, '');

    if (
      bypassPhoneNumberEntry === false &&
      (unformattedPhoneNumber.length < 10 ||
        !unformattedPhoneNumber.match(phoneNumberRegex))
    ) {
      onError(errors.INVALID_PHONE);
      return;
    }

    await fireAnalytics();
    onSuccess(unformattedPhoneNumber);
  };

  const inputProps: InputProps = {
    id,
    type: 'tel',
    className: 'fs-block',
    label: labelText,
    placeholder: placeholderText,
    options: {
      delimiters: ['(', ')', ' ', '-'],
      blocks: [0, 3, 0, 3, 4],
      numericOnly: true,
      phoneRegionCode: 'US'
    },
    onChange: (val: string) => setPhoneNumber(val),
    inputMode: 'numeric',
    pattern: '[0-9]*',
    value: phoneNumber,
    'aria-invalid': errorMessage ? true : false
  };

  const isEmailCapture = widgetType === ctaWidgetEnum.WAITLIST_EMAIL_CAPTURE;

  return (
    <Wrapper height={height} width={width}>
      <Container data-e2e="phone-container" className={className}>
        {isEmailCapture ? (
          <CtaWidgetMap
            type={widgetType}
            allProps={{ ...widgetProps, inputProps, setErrorMessage } as any}
          />
        ) : (
          <FlexForm ref={formRef} onSubmit={submit} width={width} noValidate>
            <CtaWidgetMap
              type={widgetType}
              allProps={{ ...widgetProps, inputProps } as any}
            />
          </FlexForm>
        )}
      </Container>
      {showErrorBanner && errorMessage && (
        <Error
          position={errorPosition}
          height={formRef?.current?.clientHeight}
          widgetType={widgetType}
        >
          <p>{errorMessage}</p>
        </Error>
      )}
    </Wrapper>
  );
}
